import { getDownloadURL, getStorage, ref as storageRef } from "firebase/storage";
import { useEffect, useMemo, useState } from 'react';
import { ADMIN_LAST_READ_PATH, BALANCE_PATH, CONTACT_FORM_PATH, DAILY_MEALS_PATH, FUND_ACTIVITY_PATH, MEALS_PATH, MENU_ACTIVITY_PATH, MENU_DRAFT_PATH, MENU_PATH, PERMISSIONS_PATH, RESOURCES_PATH, SCHOOL_GROUPS_PATH, SNACKS_PATH, USERS_PATH, userConfigPath, userLogsPath, userNtfNewsReadAtBasePath, userProfilePath, userSchoolsPath, userSettingsPath, userVerificationsPath, WEBHOOK_PATH } from '../../functions/src/admin/paths';
import firebase, { auth, DataSnapshot, mainDatabase, schoolDatabase } from './firebase';


export const EMPTY_FILTER: DatabaseFilter = ref => ref;

/** Wallet balance for every user lives on main RTDB (`users/{uid}/balance`), not the school instance. */
function isMainWalletBalanceRef(shortRef: string | undefined): boolean {
    if (!shortRef) return false;
    const parts = shortRef.split('/');
    return parts.length === 3 && parts[0] === USERS_PATH && parts[2] === BALANCE_PATH;
}

export function useDatabase(ref: string, filter: DatabaseFilter = EMPTY_FILTER, memoFilter = false, useMain: boolean = undefined, localDatabase: firebase.database.Database = schoolDatabase) {
    const [snapshot, setSnapshot] = useState<DataSnapshot>();

    const database = useMemo((): typeof mainDatabase => {
        if (typeof useMain === 'boolean') {
            return useMain ? mainDatabase : localDatabase
        }

        const shortRef = ref?.startsWith('/') ? ref?.replace('/', '') : ref;

        if (isMainWalletBalanceRef(shortRef)) {
            return mainDatabase;
        }

        const uid = auth.currentUser?.uid;

        const mainDB = [
            RESOURCES_PATH,
            MEALS_PATH,
            DAILY_MEALS_PATH,
            SNACKS_PATH,
            MENU_PATH,
            MENU_DRAFT_PATH,
            MENU_ACTIVITY_PATH,
            PERMISSIONS_PATH,
            FUND_ACTIVITY_PATH,
            SCHOOL_GROUPS_PATH,
            WEBHOOK_PATH,
            CONTACT_FORM_PATH,
            ADMIN_LAST_READ_PATH,
            userProfilePath(uid),
            userLogsPath(uid),
            userSchoolsPath(uid),
            userSettingsPath(uid),
            userVerificationsPath(uid),
            userConfigPath(uid),
            userNtfNewsReadAtBasePath(uid)
        ];

        for (let prefix of mainDB) {
            if (shortRef?.startsWith(prefix)) {
                return mainDatabase
            }
        }

        return localDatabase;
    }, [ref]);

    useEffect(() => {
        setSnapshot(null);
        if (!ref)
            return undefined;

        const handler = filter(database.ref(ref)).on('value', setSnapshot,
            err => { console.error(err) }
        )

        return () => {
            database.ref(ref).off('value', handler);
        }
    }, memoFilter ? [ref, filter] : [ref]);

    return snapshot;
}

export function useSnapshotState<T = any>(snapshot: DataSnapshot, name: string) {
    const sChild = name ? snapshot?.child(name) : snapshot;
    // Create stable references for the dependency array to avoid undefined issues
    const snapshotRef = snapshot || null;
    const nameRef = name || '';
    return useMemo<[T, (value: T) => void]>(() => [
        sChild?.val(),
        (value: T) => { sChild?.ref.set(value) }
    ], [snapshotRef, nameRef]);
}


export function useDatabaseVal<T = undefined>(ref: string, def: T = undefined, useMain: boolean = undefined, localDatabase: firebase.database.Database = schoolDatabase): [T, DataSnapshot, (val: T) => void] {
    const snapshot = useDatabase(ref, undefined, undefined, useMain, localDatabase);

    const onChange = (val: T) => {
        snapshot?.ref.set(val)
    };

    // Create stable reference for the dependency array to avoid undefined issues
    const snapshotRef = snapshot || null;
    return useMemo(() => [
        snapshot ? snapshot.val() as T : def,
        snapshot,
        onChange
    ], [snapshotRef]);
}

export function useList(snapshot: DataSnapshot) {
    // Create stable reference for the dependency array to avoid undefined issues
    const snapshotRef = snapshot || null;
    return useMemo(() => {
        const arr: DataSnapshot[] = [];
        snapshot?.forEach(s => { arr.push(s) });
        return arr;
    }, [snapshotRef]);
}

export function useFirebaseUser() {
    const [user, setUser] = useState<firebase.User>();

    useEffect(() => {
        return auth.onAuthStateChanged(user => {
            setUser(user);
        })
    }, []);

    return user
}

// Hook to convert Firebase Storage gs:// URL to download URL
export function useStorageUrl(storageUrl: string | null | undefined): string | null {
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!storageUrl) {
            setDownloadUrl(null);
            return;
        }

        // If it's already an HTTP URL, use it as is
        if (storageUrl.startsWith('http')) {
            setDownloadUrl(storageUrl);
            return;
        }

        // If it's a Firebase Storage gs:// URL, convert it
        if (storageUrl.startsWith('gs://')) {
            const storage = getStorage();
            const pathWithoutGs = storageUrl.replace('gs://lunchbox-catering.appspot.com/', '');
            const storageRefPath = storageRef(storage, pathWithoutGs);

            getDownloadURL(storageRefPath)
                .then((url) => {
                    setDownloadUrl(url);
                })
                .catch((error) => {
                    console.error('Error getting download URL:', error);
                    setDownloadUrl(null);
                });
        } else {
            setDownloadUrl(storageUrl);
        }
    }, [storageUrl]);

    return downloadUrl;
}

export type DatabaseFilter = (ref: firebase.database.Reference) => firebase.database.Query;
